import { useUser } from '@/components/AuthInterceptorComponent/hooks/useUser';
import { useConfig } from '@/hooks/useConfig';
import { useMetrics } from '@/hooks/useMetrics';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { bottomNavbarStyles } from './BottomNavbar.styles';
import { Home, BookOpen, GraduationCap, Link2, ListTodo } from 'lucide-react';
import { usePendingContentRedirect } from '@/hooks/usePendingContentRedirect';
import { usePendingContent } from '@/hooks';

export enum Links {
  HOME = '/',
  MY_COURSES = '/content',
  POINTS = '/points',
  PENSUMS = '/pensums',
}

const getActiveIndex = (pathname: string): number => {
  if (pathname === Links.HOME) return 0;
  if (pathname === Links.MY_COURSES) return 1;
  if (pathname === Links.POINTS) return 2;
  if (pathname === Links.PENSUMS) return 3;
  return 0;
};

export const BottomNavbar = () => {
  const query = window.location.search;
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const config = useConfig();
  const { data: user } = useUser();
  const metrics = useMetrics();

  const activeIndex = getActiveIndex(location.pathname);
  const { hasPendingContent, pendings } = usePendingContent();
  const { handlePendingContentRedirect } = usePendingContentRedirect();
  const { classes, cx } = bottomNavbarStyles({ activeIndex });

  const trackTab = (tab: string) => {
    metrics.trackEvent({
      action: 'bottom_nav_tapped',
      section: 'home',
      companies: config.slug_name ? [config.slug_name] : undefined,
      extra_data: { tab },
    });
  };

  const handleMetricsMyAccess = (url: string) => {
    trackTab('mis_accesos');
    window.location.href = url;
  };

  const isHomeActive = location.pathname === Links.HOME;
  const isMyCoursesActive = location.pathname === Links.MY_COURSES;
  const isPensumsActive = location.pathname === Links.PENSUMS;

  const iconColor = theme.palette.primary.main;
  const inactiveColor = 'rgba(0,0,0,0.38)';

  return (
    <nav className={classes.root} aria-label="Bottom navigation">
      <div className={classes.navRow}>
        {/* ── Home ─────────────────────────────────────────────────────────── */}
        <Link
          to={Links.HOME}
          onClick={() => trackTab('inicio')}
          className={cx(
            classes.navItem,
            isHomeActive ? classes.navItemActive : classes.navItemInactive,
          )}
          aria-label={t('general.home') || undefined}
          aria-current={isHomeActive ? 'page' : undefined}
        >
          <div
            className={cx(
              classes.iconWrapper,
              isHomeActive && classes.iconWrapperActive,
            )}
          >
            <Home size={24} color={isHomeActive ? iconColor : inactiveColor} />
          </div>
          <span
            className={cx(classes.label, isHomeActive && classes.labelActive)}
          >
            {t('general.home')}
          </span>
        </Link>

        {/* ── Mis rutas ────────────────────────────────────────────────────── */}
        <Link
          to={`${Links.MY_COURSES}${query}`}
          onClick={() => trackTab('mis_rutas')}
          className={cx(
            classes.navItem,
            isMyCoursesActive ? classes.navItemActive : classes.navItemInactive,
          )}
          aria-label={
            (config.isSimple
              ? t('homeScreen.myContents')
              : t(`contentPageNew.myCourses${config.isMeidei}`)) || undefined
          }
          aria-current={isMyCoursesActive ? 'page' : undefined}
        >
          <div
            className={cx(
              classes.iconWrapper,
              isMyCoursesActive && classes.iconWrapperActive,
            )}
          >
            <BookOpen
              size={24}
              color={isMyCoursesActive ? iconColor : inactiveColor}
            />
          </div>
          <span
            className={cx(
              classes.label,
              isMyCoursesActive && classes.labelActive,
            )}
          >
            {config.isSimple
              ? t('homeScreen.myContents')
              : t(`contentPageNew.myCourses${config.isMeidei}`)}
          </span>
        </Link>

        {/* ── Pensums ──────────────────────────────────────────────────────── */}
        <Link
          to={Links.PENSUMS}
          onClick={() => trackTab('pensums')}
          className={cx(
            classes.navItem,
            isPensumsActive ? classes.navItemActive : classes.navItemInactive,
          )}
          aria-label={t('homeScreen.pensums') || undefined}
          aria-current={isPensumsActive ? 'page' : undefined}
        >
          <div
            className={cx(
              classes.iconWrapper,
              isPensumsActive && classes.iconWrapperActive,
            )}
          >
            <GraduationCap
              size={24}
              color={isPensumsActive ? iconColor : inactiveColor}
            />
          </div>
          <span
            className={cx(
              classes.label,
              isPensumsActive && classes.labelActive,
            )}
          >
            {t('homeScreen.pensums')}
          </span>
        </Link>

        {/* ── Mis accesos ─────────────────────────────────────────── */}
        {Boolean(config.isMeidei) && (
          <div
            role="button"
            tabIndex={0}
            onClick={() =>
              handleMetricsMyAccess(user?.menu_links.my_certs || '')
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ')
                handleMetricsMyAccess(user?.menu_links.my_certs || '');
            }}
            className={cx(classes.navItem, classes.navItemInactive)}
            aria-label={t('sidebar.myAccess') || undefined}
          >
            <div className={classes.iconWrapper}>
              <Link2 size={24} color={inactiveColor} />
            </div>
            <span className={classes.label}>{t('sidebar.myAccess')}</span>
          </div>
        )}

        {/* ── Pendings contents ──────────────────────────────────────────────────────── */}
        <div
          onClick={() => {
            trackTab('pendings');
            handlePendingContentRedirect();
          }}
          className={cx(classes.navItem)}
          aria-label={t('general.pendings') || undefined}
        >
          <div className={cx(classes.iconWrapper)}>
            <ListTodo size={24} color={inactiveColor} />
          </div>
          <span
            className={cx(
              classes.label,
              isPensumsActive && classes.labelActive,
            )}
          >
            {t('general.pendings')}
            {hasPendingContent && pendings > 0 && (
              <span className={classes.badge}>
                {pendings > 99 ? '99+' : pendings}
              </span>
            )}
          </span>
        </div>
      </div>
    </nav>
  );
};
